import type { JSX } from 'react';
import type {
  CreateReminderBody,
  ReminderResource,
  ReminderTypeValue,
  UpdateReminderBody,
} from '@majuscule/contracts';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import {
  addDays,
  crmSend,
  errorMessage,
  nextMonday,
  parisToday,
  REMINDER_TYPE_LABEL,
  REMINDER_TYPES,
} from '../../lib/crm';
import { MutationError, useCompanies, useCompany, useCrmRefresh } from './shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHead,
} from '@/components/ui/dialog';
import { Field, FieldHint, Fields2, Fieldset } from '@/components/ui/field';
import { DateInput, TimeInput } from '@/components/ui/date-input';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { TableTabs } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';

export type ReminderDialogMode =
  | {
      kind: 'create';
      /** Null: the operator picks the company (À faire page). */
      companyId: string | null;
      dealId?: string | null;
      subject?: string;
    }
  | { kind: 'edit'; reminder: ReminderResource };

interface ReminderDialogProps {
  mode: ReminderDialogMode | null;
  onClose: () => void;
}

/** « Programmer un rappel » / « Reporter » (ReminderDialog.dc.html). */
export function ReminderDialog({ mode, onClose }: ReminderDialogProps): JSX.Element {
  return (
    <Dialog
      open={mode !== null}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      {mode !== null ? <ReminderForm mode={mode} onClose={onClose} /> : null}
    </Dialog>
  );
}

type Shortcut = 'tomorrow' | 'three' | 'monday' | 'two_weeks';

const SHORTCUTS: readonly { id: Shortcut; label: string; date: (today: string) => string }[] = [
  { id: 'tomorrow', label: 'Demain', date: (today) => addDays(today, 1) },
  { id: 'three', label: 'Dans 3 jours', date: (today) => addDays(today, 3) },
  { id: 'monday', label: 'Lundi prochain', date: nextMonday },
  { id: 'two_weeks', label: 'Dans 2 semaines', date: (today) => addDays(today, 14) },
];

function ReminderForm({
  mode,
  onClose,
}: {
  mode: ReminderDialogMode;
  onClose: () => void;
}): JSX.Element {
  const refresh = useCrmRefresh();
  const today = parisToday();
  const editing = mode.kind === 'edit' ? mode.reminder : null;

  const [companyId, setCompanyId] = useState<string>(
    mode.kind === 'edit' ? mode.reminder.companyId : (mode.companyId ?? ''),
  );
  const [dealId, setDealId] = useState<string>(
    mode.kind === 'create' ? (mode.dealId ?? '') : (mode.reminder.dealId ?? ''),
  );
  const [subject, setSubject] = useState(
    editing?.subject ?? (mode.kind === 'create' ? (mode.subject ?? '') : ''),
  );
  const [type, setType] = useState<ReminderTypeValue>(editing?.type ?? 'call');
  const [date, setDate] = useState(editing?.dueDate ?? addDays(today, 1));
  const [time, setTime] = useState(editing?.dueTime ?? '09:00');
  const [contactId, setContactId] = useState(editing?.contactId ?? '');
  const [note, setNote] = useState(editing?.note ?? '');
  const [submitted, setSubmitted] = useState(false);

  const pickCompany = mode.kind === 'create' && mode.companyId === null;
  const companies = useCompanies(pickCompany);
  const company = useCompany(companyId === '' ? null : companyId);
  const openDeals = (company.data?.deals ?? []).filter(
    (deal) => deal.stage !== 'won' && deal.stage !== 'lost',
  );

  const save = useMutation({
    mutationFn: async (): Promise<void> => {
      const fields = {
        subject: subject.trim(),
        type,
        dueDate: date,
        dueTime: time,
        contactId: contactId === '' ? null : contactId,
        note: note.trim() === '' ? null : note.trim(),
      };
      if (editing !== null) {
        const body: UpdateReminderBody = fields;
        await crmSend<ReminderResource>('PATCH', `/reminders/${editing.id}`, body);
        return;
      }
      const body: CreateReminderBody = {
        ...fields,
        companyId,
        dealId: dealId === '' ? null : dealId,
      };
      await crmSend<ReminderResource>('POST', '/reminders', body);
    },
    onSuccess: () => {
      refresh();
      onClose();
    },
  });

  const errors = {
    company: companyId === '' ? 'Choisir une entreprise.' : undefined,
    subject: subject.trim() === '' ? 'Indiquer l’objet du rappel.' : undefined,
    date: date === '' ? 'Choisir une date.' : undefined,
    time: time === '' ? 'Choisir une heure.' : undefined,
  };
  const valid = Object.values(errors).every((error) => error === undefined);
  const shown = (error: string | undefined): string | undefined => (submitted ? error : undefined);

  const description =
    company.data !== undefined
      ? [
          company.data.name,
          openDeals.find((deal) => deal.id === dealId)?.title ?? editing?.dealTitle ?? null,
        ]
          .filter((part): part is string => part !== null && part !== '')
          .join(', ')
      : undefined;

  return (
    <DialogContent aria-describedby={undefined}>
      <DialogHead
        title={editing !== null ? 'Reporter le rappel' : 'Programmer un rappel'}
        description={description}
      />
      <form
        id="reminder-form"
        className="contents"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(true);
          if (valid) {
            save.mutate();
          }
        }}
      >
        <DialogBody>
          {pickCompany ? (
            <Field label="Entreprise" required error={shown(errors.company)}>
              <NativeSelect
                value={companyId}
                onChange={(event) => {
                  setCompanyId(event.target.value);
                  setDealId('');
                  setContactId('');
                }}
              >
                <option value="">Choisir une entreprise</option>
                {(companies.data?.companies ?? []).map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </NativeSelect>
            </Field>
          ) : null}

          <Field label="Objet" required error={shown(errors.subject)}>
            <Input
              value={subject}
              maxLength={200}
              onChange={(event) => {
                setSubject(event.target.value);
              }}
            />
          </Field>

          <div className="grid gap-1.5">
            <Label id="reminder-type-label">Type</Label>
            <TableTabs<ReminderTypeValue>
              aria-label="Type de rappel"
              className="justify-self-start"
              value={type}
              onValueChange={setType}
              items={REMINDER_TYPES.map((value) => ({
                value,
                label: REMINDER_TYPE_LABEL[value],
              }))}
            />
          </div>

          <Fieldset title="Échéance">
            <div role="group" aria-label="Raccourcis de date" className="flex flex-wrap gap-1.5">
              {SHORTCUTS.map((shortcut) => {
                const value = shortcut.date(today);
                const pressed = value === date;
                return (
                  <Button
                    key={shortcut.id}
                    type="button"
                    variant="outline"
                    size="xs"
                    aria-pressed={pressed}
                    className="aria-pressed:border-foreground aria-pressed:bg-accent"
                    onClick={() => {
                      setDate(value);
                    }}
                  >
                    {shortcut.label}
                  </Button>
                );
              })}
            </div>
            <Fields2>
              <Field label="Date" required error={shown(errors.date)}>
                <DateInput
                  value={date}
                  onChange={(value) => {
                    setDate(value);
                  }}
                />
              </Field>
              <Field label="Heure" required error={shown(errors.time)}>
                <TimeInput
                  value={time}
                  onChange={(value) => {
                    setTime(value);
                  }}
                />
              </Field>
            </Fields2>
          </Fieldset>

          <Fields2>
            <Field label="Contact">
              <NativeSelect
                value={contactId}
                onChange={(event) => {
                  setContactId(event.target.value);
                }}
              >
                <option value="">Aucun contact</option>
                {(company.data?.contacts ?? []).map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {contact.firstName} {contact.lastName}
                    {contact.jobTitle !== null ? `, ${contact.jobTitle.toLowerCase()}` : ''}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            {editing === null ? (
              <Field label="Affaire">
                <NativeSelect
                  value={dealId}
                  onChange={(event) => {
                    setDealId(event.target.value);
                  }}
                >
                  <option value="">Aucune affaire</option>
                  {openDeals.map((deal) => (
                    <option key={deal.id} value={deal.id}>
                      {deal.title}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
            ) : null}
          </Fields2>

          <Field label="Note">
            <Textarea
              rows={3}
              maxLength={2000}
              placeholder="Ce qu’il faut avoir en tête au moment du rappel"
              value={note}
              onChange={(event) => {
                setNote(event.target.value);
              }}
            />
          </Field>

          <FieldHint>
            Le rappel s’affiche dans À faire. S’il est le plus proche, il devient la prochaine
            action de l’affaire.
          </FieldHint>
          <MutationError
            message={
              save.isError ? errorMessage(save.error, 'Échec de l’enregistrement du rappel.') : null
            }
          />
        </DialogBody>
      </form>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Annuler
        </Button>
        <Button type="submit" form="reminder-form" size="lg" disabled={save.isPending}>
          {editing !== null ? 'Reporter' : 'Programmer'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
